import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';

import { activeDialogSurface, isStepOnScreen } from './dom';
import { HelpDrawer } from './HelpDrawer';
import { HelpInspect } from './HelpInspect';
import { HelpTour } from './HelpTour';
import {
  readFloatingPref,
  readLanguagePref,
  writeFloatingPref,
  writeLanguagePref,
} from './preference';
import { helpForPath } from './registry';
import { uiText } from './text';
import type { HelpLanguage, HelpStep, HelpViewer } from './types';
import { localizedContext, localizedContexts, localizedPage, localizedStep, stepVisibleForRole } from './types';

/**
 * The entry point to the page guide (spec §9.5) — two of them, in fact:
 *
 *  - a DOCKED "?" the shell places in the top bar's right cluster, next to the bell. Always there,
 *    always clickable — including over an open dialog, which takes the portaled copy below — and
 *    unlike a floating button it never sits on top of the page's own controls.
 *  - a FLOATING "?" in the bottom-right corner. Opt-in (see ./preference), because that is where
 *    the list screens keep their pagination and row actions.
 *
 * `pathname` and the viewer's role flags arrive as props rather than being read from the router
 * and the Jotai store in here, so the whole `help/` folder stays importable from a test or a Node
 * script with no providers around it.
 */

/** A message the guide wants shown to the user, outside its own panels. */
export interface HelpNotice {
  severity: 'info' | 'warning' | 'success';
  message: string;
}

/** The drawer's close transition — starting the tour sooner spotlights elements under the panel. */
const DRAWER_CLOSE_MS = 260;

/**
 * Whether a modal dialog is on screen right now.
 *
 * Watched rather than measured once, because the "?" has to move above the backdrop the moment a
 * dialog takes the screen over. `childList` + `subtree` is enough: MUI mounts a dialog into a
 * fresh portal container and unmounts it on close.
 */
const useDialogOpen = (): boolean => {
  const [open, setOpen] = useState(false);
  const last = useRef(false);

  useEffect(() => {
    const read = (): void => {
      const next = activeDialogSurface() !== null;
      // Guarded: the observer hears every grid re-render, and this must not dispatch a state
      // update per mutation batch.
      if (next === last.current) return;
      last.current = next;
      setOpen(next);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return open;
};

interface HelpLauncherProps {
  pathname: string;
  viewer: HelpViewer;
  /** The app's toast system; falls back to the launcher's own Snackbar. */
  notify?: (notice: HelpNotice) => void;
}

export const HelpLauncher = ({ pathname, viewer, notify }: HelpLauncherProps): JSX.Element | null => {
  // Keyed to the route rather than a boolean: navigating away closes the panel by definition,
  // with no effect to keep in sync.
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [tour, setTour] = useState<{
    steps: HelpStep[];
    scope: HTMLElement | null;
    /** Which of them to open on — a reader who picked a field does not start at step 1. */
    startIndex: number;
  } | null>(null);
  /** Click-to-explain: the page stays visible and inert while the user points at controls. */
  const [inspecting, setInspecting] = useState(false);
  /** Why the guide could not do what was asked — an empty tour, or a step that is not rendered. */
  const [notice, setNotice] = useState<HelpNotice | null>(null);
  /** Read once on mount — the preference only ever changes through the controls below. */
  const [floating, setFloating] = useState(readFloatingPref);
  /**
   * The reader's language, owned here rather than in the drawer because all three surfaces answer
   * to it: switching to German in the drawer and then starting a tour that walks on in English
   * would read as the setting not having taken.
   */
  const [language, setLanguage] = useState<HelpLanguage>(readLanguagePref);
  /** Shown when the floating button is switched off, so nobody thinks the guide went with it. */
  const [dismissed, setDismissed] = useState(false);
  const dialogOpen = useDialogOpen();
  /** Where the docked button sits, so the copy that outranks a dialog can stand on it. */
  const dockRef = useRef<HTMLButtonElement>(null);
  const [dockRect, setDockRect] = useState<DOMRect | null>(null);

  /**
   * The app bar is its own stacking context, so the docked "?" cannot be lifted over a dialog's
   * backdrop from inside it. While a dialog is up, the button is therefore re-rendered as a
   * portaled copy pinned to the same rectangle, which the backdrop cannot cover.
   */
  useEffect(() => {
    // The rect is only read while a dialog is open, so nothing needs resetting when it closes.
    if (!dialogOpen) return;
    const measure = (): void => {
      const el = dockRef.current;
      if (el) setDockRect(el.getBoundingClientRect());
    };
    const frame = window.requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
    };
  }, [dialogOpen]);

  const help = helpForPath(pathname);

  const roleSteps = useMemo(
    () => (help ? help.steps.filter((step) => stepVisibleForRole(step, viewer)) : []),
    [help, viewer],
  );

  // An unregistered route has no guide and therefore no button.
  if (!help) return null;

  const open = openFor === pathname;
  const text = uiText(language);
  const pageTitle = localizedPage(help, language).title;
  const announce: (next: HelpNotice) => void = notify ?? setNotice;

  const chooseFloating = (on: boolean): void => {
    setFloating(on);
    writeFloatingPref(on);
    setDismissed(!on);
  };

  const chooseLanguage = (next: HelpLanguage): void => {
    setLanguage(next);
    writeLanguagePref(next);
  };

  // Hidden while the panel, a tour or the inspector is up: over the page being explained it is
  // one more thing in the way.
  const showFloating = floating && !open && tour === null && !inspecting;

  /** Open the tour — from the beginning, or at the step the reader picked in the panel or inspector. */
  const startTour = (from?: HelpStep): void => {
    setOpenFor(null);
    setInspecting(false);
    // Measured after the drawer has closed: the panel would otherwise cover the elements being
    // spotlighted, and a step hidden behind it would measure as present.
    window.setTimeout(() => {
      // A tour started over an open dialog stays INSIDE it — the page behind the backdrop cannot
      // be clicked, and the dialog is usually what the guide was opened for.
      const surface = activeDialogSurface();
      // Role-visible AND actually rendered within that surface, so the tour never spotlights a
      // hole where a dialog field or an empty-state button would be.
      const visible = roleSteps.filter((step) => isStepOnScreen(step, surface ?? document));
      if (visible.length === 0) {
        announce({ severity: 'info', message: surface ? text.tourEmptyDialog : text.tourEmptyPage });
        return;
      }
      const startIndex = from ? visible.indexOf(from) : 0;
      if (from && startIndex < 0) {
        // The drawer lists the WHOLE guide, including controls behind a UI action — say which,
        // and how to reach it, rather than silently starting the tour somewhere else.
        const title = localizedStep(from, language).title;
        const hint = from.context ? localizedContext(help, from.context, language) : undefined;
        announce({
          severity: 'info',
          message: hint ? text.tourStepHidden(title, hint) : text.tourStepOffScreen(title),
        });
        return;
      }
      setTour({ steps: visible, scope: surface, startIndex: Math.max(startIndex, 0) });
    }, DRAWER_CLOSE_MS);
  };

  /** Hand over to click-to-explain. Same delay as the tour, and for the same reason. */
  const startInspect = (): void => {
    setOpenFor(null);
    setTour(null);
    window.setTimeout(() => setInspecting(true), DRAWER_CLOSE_MS);
  };

  return (
    <>
      <Tooltip title={dialogOpen ? '' : text.launcherTooltip(pageTitle)}>
        <IconButton
          ref={dockRef}
          size="small"
          color={open ? 'primary' : 'default'}
          aria-label={text.launcherAria(pageTitle)}
          data-help="shell-help"
          onClick={() => setOpenFor(open ? null : pathname)}
          // While the elevated copy stands on top of it, this one is inert: behind the backdrop it
          // could not be clicked anyway, and the accessibility tree would announce it twice.
          {...(dialogOpen ? { 'aria-hidden': true, tabIndex: -1 } : {})}
          // The top bar's bordered 38 px square, like the bell beside it.
          sx={{
            width: 38,
            height: 38,
            border: 1,
            borderColor: 'divider',
            bgcolor: open ? 'spectrum.tint' : 'background.paper',
            ...(dialogOpen ? { visibility: 'hidden' } : {}),
          }}
        >
          <HelpOutlineOutlinedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>

      {dialogOpen && (
        <Portal>
          <Tooltip title={text.launcherTooltip(pageTitle)}>
            <IconButton
              size="small"
              aria-label={text.launcherAria(pageTitle)}
              data-help-elevated="true"
              onClick={() => setOpenFor(open ? null : pathname)}
              sx={{
                position: 'fixed',
                ...(dockRect
                  ? {
                      top: dockRect.top,
                      left: dockRect.left,
                      width: dockRect.width,
                      height: dockRect.height,
                    }
                  : // Only if the measurement failed: roughly where the app bar keeps it.
                    { top: 8, right: 120 }),
                zIndex: (t) => t.zIndex.modal + 1,
                color: 'primary.main',
                bgcolor: 'background.paper',
                boxShadow: '0 2px 10px rgba(31, 47, 69, 0.35)',
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <HelpOutlineOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Portal>
      )}

      {showFloating && (
        // Portaled to the body: the app bar is its own stacking context, and this button has to
        // stay usable over a dialog too.
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: (t) => t.zIndex.modal + 1,
              // The dismiss badge is a hover affordance — always-on it would read as part of the
              // button and invite a mis-click.
              '&:hover .help-fab-dismiss, &:focus-within .help-fab-dismiss': {
                opacity: 1,
                pointerEvents: 'auto',
              },
            }}
          >
            <Tooltip title={text.launcherTooltip(pageTitle)} placement="left">
              <Fab
                color="primary"
                size="medium"
                aria-label={text.floatingAria}
                onClick={() => setOpenFor(open ? null : pathname)}
                sx={{ boxShadow: '0 6px 18px rgba(31, 47, 69, 0.22)' }}
              >
                <HelpOutlineOutlinedIcon />
              </Fab>
            </Tooltip>

            <Tooltip title={text.floatingHide} placement="top">
              <IconButton
                className="help-fab-dismiss"
                size="small"
                aria-label={text.floatingHideAria}
                onClick={() => chooseFloating(false)}
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  width: 22,
                  height: 22,
                  // Dark on purpose: a white badge disappeared into the page behind it.
                  bgcolor: '#1F2F45',
                  color: 'common.white',
                  border: '2px solid',
                  borderColor: 'background.paper',
                  opacity: 0,
                  pointerEvents: 'none',
                  transition: 'opacity 120ms ease',
                  '&:hover': { bgcolor: '#1F2F45', color: 'common.white' },
                }}
              >
                <CloseOutlinedIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Portal>
      )}

      <HelpDrawer
        open={open}
        help={help}
        steps={roleSteps}
        floating={floating}
        onFloatingChange={chooseFloating}
        language={language}
        onLanguageChange={chooseLanguage}
        onClose={() => setOpenFor(null)}
        onStartTour={startTour}
        onInspect={startInspect}
      />

      {tour && (
        <HelpTour
          steps={tour.steps}
          scope={tour.scope}
          startIndex={tour.startIndex}
          help={help}
          language={language}
          onInspect={startInspect}
          onClose={() => setTour(null)}
        />
      )}

      {inspecting && (
        <HelpInspect
          steps={roleSteps}
          help={help}
          language={language}
          contexts={localizedContexts(help, language)}
          onTourFrom={startTour}
          onExit={() => setInspecting(false)}
        />
      )}

      <Snackbar
        open={notice !== null}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={notice?.severity ?? 'info'} onClose={() => setNotice(null)}>
          {notice?.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={dismissed}
        autoHideDuration={7000}
        onClose={() => setDismissed(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setDismissed(false)}
          action={
            <Button color="inherit" size="small" onClick={() => chooseFloating(true)}>
              {text.undo}
            </Button>
          }
        >
          {text.floatingHidden}
        </Alert>
      </Snackbar>
    </>
  );
};
